import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Flex, Stack, Tabs, useTheme, spacing } from '@fti/design-system';
import { PublicationSelector } from '../shared/PublicationSelector';
import { StatusBadge } from '../shared/StatusBadge';
import { HelpText } from '../shared/HelpText';
import { StartExecutionButton } from '../executions/StartExecutionButton';
import { ExecutionStatus } from '../executions/ExecutionStatus';
import {
  useRun,
  useUpdateRun,
  useRunPublications,
  useSetRunPublications,
  useRunMessages,
  useSetRunMessages,
} from '../../../api/media-sim/runs';
import { useApiData } from '../../../api/client';
import type { RunExecution } from '../../../types/media-sim';
import type {
  Message,
  RunUpdateRequest,
  RunValidationResult,
  Publication,
} from '../../../types/media-sim';

const EVENT_CONTEXT_EXAMPLES = `Example 1 — Consumer Reaction:
"Following the announcement, a coalition of consumer advocacy groups launched a boycott campaign on social media, calling on customers to stop purchasing products from both companies until the deal terms are made public."

Example 2 — Competitor Statement:
"Hours after the deal was announced, Kraft Heinz issued a statement saying it remains 'well-positioned to compete' and is evaluating its own strategic options in the condiments and packaged food space."`;

interface RunFormProps {
  projectId: string;
  runId: string;
  projectPublications: Publication[];
  projectMessages: Message[];
  onBack: () => void;
  onValidate: () => void;
  validationResult?: RunValidationResult;
}

export function RunForm({
  projectId,
  runId,
  projectPublications,
  projectMessages,
  onBack,
  onValidate,
  validationResult,
}: RunFormProps) {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { data: run } = useRun(projectId, runId);
  const { data: runPubs } = useRunPublications(projectId, runId);
  const { data: runMsgs } = useRunMessages(projectId, runId);
  const { updateRun } = useUpdateRun();
  const { setPublications } = useSetRunPublications();
  const { setMessages } = useSetRunMessages();

  const [runName, setRunName] = useState('');
  const [eventContext, setEventContext] = useState('');
  const [numArticles, setNumArticles] = useState(5);
  const [selectedPubIds, setSelectedPubIds] = useState<string[]>([]);
  const [selectedMsgIds, setSelectedMsgIds] = useState<string[]>([]);
  const [activeExecutionId, setActiveExecutionId] = useState<string | null>(null);
  // Which execution's status is shown (Sample | Full toggle).
  const [statusTab, setStatusTab] = useState<'sample' | 'full'>('sample');
  const { data: executions, refetch: refetchExecutions } = useApiData<RunExecution[]>(
    `/media-sim/projects/${projectId}/runs/${runId}/executions`
  );

  const sampleExecution = executions?.find((e) => e.execution_type === 'sample') ?? null;
  const fullExecution = executions?.find((e) => e.execution_type === 'full') ?? null;

  // Config fields (publications, messages, event context, article count) stay
  // editable until the full simulation runs — a sample doesn't lock them.
  const isLocked =
    run?.status === 'full_running' || run?.status === 'full_completed';
  // The run name, however, locks as soon as any execution exists (a sample
  // establishes the run's identity), so results always map to a stable name.
  const hasAnyExecution = !!executions && executions.length > 0;
  const nameLocked = hasAnyExecution || isLocked;

  // When an execution is (re)started, immediately refetch the list so the new
  // record shows up — then select it so the status toggle jumps to it.
  const handleExecutionCreated = useCallback((execId: string) => {
    setActiveExecutionId(execId);
    refetchExecutions();
  }, [refetchExecutions]);

  // Poll the executions list while anything is pending/running so status
  // transitions (pending → running → completed) surface without a reload.
  useEffect(() => {
    const active = executions?.some((e) => e.status === 'pending' || e.status === 'running');
    if (!active) return;
    const t = setInterval(() => refetchExecutions(), 5000);
    return () => clearInterval(t);
  }, [executions, refetchExecutions]);

  // Auto-load the most recent execution if one exists
  useEffect(() => {
    if (!activeExecutionId && executions && executions.length > 0) {
      setActiveExecutionId(executions[0].id);
    }
  }, [executions, activeExecutionId]);

  // Follow the active execution's type with the status toggle (e.g. when the
  // user starts a full simulation, switch the toggle to Full).
  useEffect(() => {
    if (!activeExecutionId || !executions) return;
    const active = executions.find((e) => e.id === activeExecutionId);
    if (active) setStatusTab(active.execution_type);
  }, [activeExecutionId, executions]);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (run) {
      setRunName(run.run_name || '');
      setEventContext(run.event_context || '');
      setNumArticles(run.number_of_articles);
    }
  }, [run]);

  useEffect(() => {
    if (runPubs) setSelectedPubIds(runPubs.map((p) => p.id));
  }, [runPubs]);

  useEffect(() => {
    if (runMsgs) setSelectedMsgIds(runMsgs.map((m) => m.id));
  }, [runMsgs]);

  const debouncedUpdate = useCallback(
    (data: RunUpdateRequest) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        try {
          await updateRun(projectId, runId, data);
        } catch { /* silent */ }
      }, 500);
    },
    [projectId, runId, updateRun]
  );

  const handlePubChange = async (ids: string[]) => {
    setSelectedPubIds(ids);
    try {
      await setPublications(projectId, runId, ids);
    } catch { /* silent */ }
  };

  const handleMsgToggle = async (msgId: string) => {
    const newIds = selectedMsgIds.includes(msgId)
      ? selectedMsgIds.filter((id) => id !== msgId)
      : [...selectedMsgIds, msgId];
    setSelectedMsgIds(newIds);
    try {
      await setMessages(projectId, runId, newIds);
    } catch { /* silent */ }
  };

  const pubsForSelector = projectPublications;

  const inputStyle = {
    width: '100%',
    padding: `${spacing.xs} ${spacing.sm}`,
    border: `1px solid ${theme.colors.border.default}`,
    borderRadius: '4px',
    backgroundColor: theme.colors.background.paper,
    color: theme.colors.text.primary,
    fontSize: '0.9rem',
  };

  const labelStyle = {
    display: 'block' as const,
    marginBottom: spacing.xs,
    fontSize: '0.85rem',
    fontWeight: 500 as const,
    color: theme.colors.text.primary,
  };

  return (
    <Stack gap="md">
      <Flex justify="start" align="center">
        <Button variant="ghost" onClick={onBack}>
          &larr; Back to Runs
        </Button>
      </Flex>

      {isLocked && (
        <Card variant="outlined">
          <Card.Body>
            <p style={{ color: theme.colors.semantic.info.default, margin: 0, fontSize: '0.85rem' }}>
              This run is locked — full simulation is {run?.status === 'full_running' ? 'in progress' : 'complete'}. Configuration can no longer be modified.
            </p>
          </Card.Body>
        </Card>
      )}

      <h2 style={{ fontSize: '1.4rem', fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>
        Run Configuration
      </h2>
      <Card variant="elevated">
        <Card.Body>
          <Stack gap="md">
            <div>
              <label style={labelStyle}>Run Name *</label>
              <input
                type="text"
                value={runName}
                disabled={nameLocked}
                onChange={(e) => {
                  setRunName(e.target.value);
                  debouncedUpdate({ run_name: e.target.value });
                }}
                placeholder="Enter run name"
                style={{ ...inputStyle, opacity: nameLocked ? 0.6 : 1 }}
              />
              {nameLocked && !isLocked && (
                <p style={{ fontSize: '0.8rem', color: theme.colors.text.secondary, margin: '2px 0 0' }}>
                  Run name is locked once a simulation has been started.
                </p>
              )}
            </div>

            <div>
              <label style={labelStyle}>Publications *</label>
              <HelpText>Select the publications you want to simulate coverage from.</HelpText>
              <PublicationSelector
                publications={pubsForSelector}
                selectedIds={selectedPubIds}
                onChange={handlePubChange}
              />
            </div>

            <div>
              <label style={labelStyle}>Messages (optional)</label>
              <HelpText>
                Select the registered messages to test in this run. Each message is
                evaluated separately by the media agents — every selected publication
                generates coverage for each message independently, so results are
                broken out per message.
              </HelpText>
              {projectMessages.length === 0 ? (
                <p style={{ fontSize: '0.85rem', color: theme.colors.text.secondary, margin: 0 }}>
                  No registered messages yet. Add an event context or go to Messages to create and register messages.
                </p>
              ) : (
                <Card variant="outlined">
                  <Card.Body>
                    {projectMessages.map((msg) => (
                      <Flex
                        key={msg.id}
                        align="center"
                        gap="sm"
                        style={{
                          padding: `${spacing.xs} ${spacing.sm}`,
                          borderBottom: `1px solid ${theme.colors.border.default}`,
                          cursor: 'pointer',
                        }}
                        onClick={() => handleMsgToggle(msg.id)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedMsgIds.includes(msg.id)}
                          onChange={() => handleMsgToggle(msg.id)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.9rem' }}>{msg.message_name}</span>
                        <StatusBadge status={msg.status} />
                      </Flex>
                    ))}
                  </Card.Body>
                </Card>
              )}
            </div>

            <div>
              <label style={labelStyle}>Event Context (optional)</label>
              <HelpText
                collapsible={{
                  label: 'Show examples',
                  content: EVENT_CONTEXT_EXAMPLES,
                }}
              >
                You may inject additional scenario information or context that is only provided to journalist agents
                during this specific simulation run. This is useful for testing how coverage changes under different
                circumstances.
              </HelpText>
              <textarea
                value={eventContext}
                disabled={isLocked}
                onChange={(e) => {
                  setEventContext(e.target.value);
                  debouncedUpdate({ event_context: e.target.value || null });
                }}
                placeholder="Enter event context — basic facts of the situation, additional context, key facts, etc."
                rows={6}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', minHeight: '150px', opacity: isLocked ? 0.6 : 1 }}
              />
            </div>

            {selectedMsgIds.length === 0 && !eventContext.trim() && (
              <Card variant="outlined">
                <Card.Body>
                  <p style={{ color: theme.colors.semantic.warning.default, margin: 0, fontSize: '0.85rem' }}>
                    A run must have at least one message or an event context to execute a simulation.
                  </p>
                </Card.Body>
              </Card>
            )}

            <div>
              <label style={labelStyle}>Number of Articles * (2-10)</label>
              <HelpText>
                How many articles each selected publication generates per message. A
                higher count gives a broader sample of how an outlet might cover the
                story, but increases generation time and cost. The sample run always
                produces a small preview regardless of this setting.
              </HelpText>
              <input
                type="number"
                min={2}
                max={10}
                value={numArticles}
                disabled={isLocked}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setNumArticles(val);
                  if (val >= 2 && val <= 10) {
                    debouncedUpdate({ number_of_articles: val });
                  }
                }}
                style={{ ...inputStyle, width: '120px', opacity: isLocked ? 0.6 : 1 }}
              />
              {(numArticles < 2 || numArticles > 10) && (
                <p style={{ fontSize: '0.8rem', color: theme.colors.semantic.error.default, marginTop: '2px' }}>
                  Must be between 2 and 10.
                </p>
              )}
            </div>
          </Stack>
        </Card.Body>
      </Card>

      {/* Execution actions — validate the config, then run a sample, then the
          full simulation. Start buttons stay disabled until validation passes.
          Article results live on the Results page (linked below), not here. */}
      {executions && (() => {
        const hasRunningExecution = executions.some((e) => e.status === 'pending' || e.status === 'running');
        const sampleCompleted = executions.some((e) => e.execution_type === 'sample' && e.status === 'completed');
        const sampleExec = executions.find((e) => e.execution_type === 'sample' && e.status === 'completed');
        // A sample in ANY state means a re-run would replace it (backend hard-deletes
        // the prior sample + its results), so we relabel and confirm.
        const sampleExists = executions.some((e) => e.execution_type === 'sample');
        const fullExists = executions.some((e) => e.execution_type === 'full');
        const isValid = !!validationResult?.valid;

        // Once the full simulation is running/complete the run is locked — keep
        // the box visible as a stable anchor, but disable all start actions.
        const sampleDisabled = isLocked || !isValid || hasRunningExecution || fullExists;
        const fullDisabled = isLocked || !isValid || hasRunningExecution || !sampleCompleted || fullExists;

        const stepStyle = { margin: 0, fontSize: '0.85rem', color: theme.colors.text.secondary };
        const numStyle = { fontWeight: 600 as const, color: theme.colors.text.primary };

        return (
          <Card variant="elevated">
            <Card.Header title="Run Simulation" />
            <Card.Body>
              <Stack gap="md">
                {/* Follow-along instructions */}
                <Stack gap="xs">
                  <p style={stepStyle}><span style={numStyle}>1.</span> Validate the run configuration above.</p>
                  <p style={stepStyle}><span style={numStyle}>2.</span> Start a sample execution — a quick, low-cost preview of the coverage.</p>
                  <p style={stepStyle}><span style={numStyle}>3.</span> Review the sample results, then start the full simulation.</p>
                  <p style={stepStyle}>
                    <span style={numStyle}>Note:</span> a run keeps a single sample — re-running the sample
                    overwrites the previous one (its results are permanently replaced). The full simulation
                    can only be run once.
                  </p>
                </Stack>

                {/* Validation feedback */}
                {validationResult && !validationResult.valid && (
                  <Card variant="outlined">
                    <Card.Body>
                      <Stack gap="xs">
                        {validationResult.errors.map((err, i) => (
                          <p key={i} style={{ color: theme.colors.semantic.error.default, margin: 0, fontSize: '0.85rem' }}>
                            {err}
                          </p>
                        ))}
                      </Stack>
                    </Card.Body>
                  </Card>
                )}
                {isValid && (
                  <p style={{ color: theme.colors.semantic.success.default, margin: 0, fontSize: '0.85rem' }}>
                    ✓ Configuration is valid and ready for execution.
                  </p>
                )}

                {/* Action buttons */}
                <Flex gap="sm" align="center" wrap="wrap">
                  <Button variant="solid" color="primary" onClick={onValidate} disabled={isLocked}>
                    Validate Run
                  </Button>
                  <StartExecutionButton
                    projectId={projectId}
                    runId={runId}
                    fixedType="sample"
                    label={sampleExists ? 'Re-run Sample' : 'Start Sample Execution'}
                    disabled={sampleDisabled}
                    confirm={sampleExists ? {
                      title: 'Re-run Sample?',
                      message: 'Re-running replaces the current sample — its articles and results will be permanently deleted and regenerated. This cannot be undone.',
                      confirmLabel: 'Replace Sample',
                    } : undefined}
                    onExecutionCreated={handleExecutionCreated}
                  />
                  <StartExecutionButton
                    projectId={projectId}
                    runId={runId}
                    fixedType="full"
                    label="Start Full Simulation"
                    disabled={fullDisabled}
                    onExecutionCreated={handleExecutionCreated}
                  />
                  {sampleExec && (
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/simulations/results?runId=${runId}&executionId=${sampleExec.id}`)}
                    >
                      View Sample Results
                    </Button>
                  )}
                </Flex>

                {isLocked ? (
                  <p style={{ margin: 0, fontSize: '0.8rem', color: theme.colors.text.secondary }}>
                    Full simulation {run?.status === 'full_running' ? 'is in progress' : 'is complete'} — no further runs can be started for this run.
                  </p>
                ) : !isValid ? (
                  <p style={{ margin: 0, fontSize: '0.8rem', color: theme.colors.text.secondary }}>
                    Run validation to enable the execution buttons.
                  </p>
                ) : !sampleCompleted && !hasRunningExecution ? (
                  <p style={{ margin: 0, fontSize: '0.8rem', color: theme.colors.text.secondary }}>
                    Start with a sample — the full simulation unlocks once a sample completes.
                  </p>
                ) : null}
              </Stack>
            </Card.Body>
          </Card>
        );
      })()}

      {/* Execution status — a design-system Tabs set (Sample | Full) below the
          section title. Each tab's content is its own execution's status; Full
          shows a placeholder until it has been run. */}
      {(sampleExecution || fullExecution) && (
        <>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 600, color: theme.colors.text.primary, margin: `${spacing.lg} 0 0` }}>
            Execution Status
          </h2>
          <Tabs value={statusTab} onChange={(v) => setStatusTab(v as 'sample' | 'full')}>
            <Tabs.List>
              <Tabs.Trigger value="sample">
                <Flex align="center" gap="xs" style={{ display: 'inline-flex' }}>
                  Sample
                  {sampleExecution && <StatusBadge status={sampleExecution.status} size="sm" />}
                </Flex>
              </Tabs.Trigger>
              <Tabs.Trigger value="full">
                <Flex align="center" gap="xs" style={{ display: 'inline-flex' }}>
                  Full Simulation
                  {fullExecution && <StatusBadge status={fullExecution.status} size="sm" />}
                </Flex>
              </Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value="sample">
              {sampleExecution ? (
                <ExecutionStatusPanel
                  projectId={projectId}
                  runId={runId}
                  executionId={sampleExecution.id}
                  onViewResults={() => navigate(`/simulations/results?runId=${runId}&executionId=${sampleExecution.id}`)}
                  onRetried={refetchExecutions}
                />
              ) : (
                <ExecutionPlaceholder text="No sample has been run yet. Validate the configuration and start a sample above." />
              )}
            </Tabs.Content>

            <Tabs.Content value="full">
              {fullExecution ? (
                <ExecutionStatusPanel
                  projectId={projectId}
                  runId={runId}
                  executionId={fullExecution.id}
                  onViewResults={() => navigate(`/simulations/results?runId=${runId}&executionId=${fullExecution.id}`)}
                  onRetried={refetchExecutions}
                />
              ) : (
                <ExecutionPlaceholder
                  text={
                    sampleExecution?.status === 'completed'
                      ? 'The full simulation has not been run yet. Use “Start Full Simulation” above to run it across all articles.'
                      : 'The full simulation has not been run yet. Complete a sample first, then start the full simulation.'
                  }
                />
              )}
            </Tabs.Content>
          </Tabs>
        </>
      )}
    </Stack>
  );
}

function ExecutionStatusPanel({
  projectId,
  runId,
  executionId,
  onViewResults,
  onRetried,
}: {
  projectId: string;
  runId: string;
  executionId: string;
  onViewResults: () => void;
  onRetried?: () => void;
}) {
  return (
    <Stack gap="md">
      <ExecutionStatus
        projectId={projectId}
        runId={runId}
        executionId={executionId}
        onRetried={onRetried}
        showResults={false}
        hideTitle
      />
      <Flex justify="end">
        <Button variant="outline" size="sm" onClick={onViewResults}>
          View Results &amp; Articles
        </Button>
      </Flex>
    </Stack>
  );
}

function ExecutionPlaceholder({ text }: { text: string }) {
  const { theme } = useTheme();
  return (
    <Card variant="outlined">
      <Card.Body>
        <p style={{ margin: 0, fontSize: '0.9rem', color: theme.colors.text.secondary }}>{text}</p>
      </Card.Body>
    </Card>
  );
}
